import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import '../styles/AssignSurveyModal.css';

const AssignSurveyModal = ({ onClose, selectedPatient = null, onSurveyAssigned }) => {
  const { patients, assignSurveyToPatient, surveyTemplates } = useAuth();
  const [formData, setFormData] = useState({
    patientId: selectedPatient?.id || '',
    surveyType: '',
    surveyTitle: '', // Custom title for this survey instance
    dueDate: '',
    priority: 'medium',
    notes: '',
    repeat: 'none', // 'none', 'weekly', 'biweekly', 'monthly'
    repeatEndDate: '' // Optional end date for recurring surveys
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Use surveyTemplates from context instead of hardcoded list

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Validation
      if (!formData.patientId || !formData.surveyType || !formData.dueDate) {
        setError('Please fill in all required fields');
        setLoading(false);
        return;
      }

      // If repeat is set but no end date is provided, use a default of 3 months from now
      let repeatEndDate = formData.repeatEndDate;
      if (formData.repeat !== 'none' && !formData.repeatEndDate) {
        const endDate = new Date();
        endDate.setMonth(endDate.getMonth() + 3);
        repeatEndDate = endDate.toISOString().split('T')[0];
      }

      const selectedSurvey = surveyTemplates?.find(s => s.id === formData.surveyType);
      if (!selectedSurvey) {
        throw new Error('Selected survey not found in templates');
      }

      const selectedPatientData = patients?.find(p => p.id.toString() === formData.patientId.toString());
      if (!selectedPatientData) {
        throw new Error('Selected patient not found');
      }

      console.log('Creating survey assignment with data:', {
        patientId: formData.patientId,
        surveyId: formData.surveyType,
        surveyTitle: formData.surveyTitle || selectedSurvey?.name || 'Survey',
        repeat: formData.repeat,
        repeatEndDate: repeatEndDate
      });

      const surveyAssignment = {
        patientId: formData.patientId,
        patientName: selectedPatientData?.name || 'Unknown Patient',
        surveyId: formData.surveyType,
        surveyName: selectedSurvey.name,
        surveyTitle: formData.surveyTitle || selectedSurvey.name,
        surveyDescription: selectedSurvey.description || '',
        estimatedTime: selectedSurvey?.estimatedTime || '10-15 minutes',
        assignedDate: new Date().toISOString(),
        dueDate: formData.dueDate,
        priority: formData.priority,
        notes: formData.notes,
        status: 'assigned',
        completedDate: null,
        responses: null,
        instanceId: `instance-${Date.now()}`,
        // Recurring survey fields
        repeat: formData.repeat,
        repeatEndDate: repeatEndDate,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      console.log('Attempting to assign survey with data:', surveyAssignment);

      // Assign survey to patient
      if (!assignSurveyToPatient) {
        throw new Error('assignSurveyToPatient function is not available');
      }

      try {
        const result = await assignSurveyToPatient(surveyAssignment);
        console.log('Survey assignment successful:', result);
        
        if (onSurveyAssigned) {
          onSurveyAssigned(result);
        }
        
        alert(`Survey "${selectedSurvey.name}" has been assigned to ${selectedPatientData.name}!`);
        onClose();
      } catch (assignError) {
        console.error('Error in assignSurveyToPatient:', assignError);
        throw assignError; // Re-throw to be caught by the outer catch
      }
    } catch (err) {
      console.error('Error in handleSubmit:', {
        name: err.name,
        message: err.message,
        stack: err.stack
      });
      setError(`Failed to assign survey: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content assign-survey-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Assign Survey to Patient</h2>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>

        <form onSubmit={handleSubmit} className="assign-survey-form">
          <div className="form-group">
            <label htmlFor="surveyType">Select Survey *</label>
            <select
              id="surveyType"
              name="surveyType"
              value={formData.surveyType}
              onChange={handleChange}
              required
              className="survey-select"
            >
              <option value="">Select a survey...</option>
              {surveyTemplates.map((survey) => (
                <option key={survey.id} value={survey.id}>
                  {survey.name}
                </option>
              ))}
            </select>
            
            {formData.surveyType && (
              <div className="survey-details">
                <p className="survey-description">
                  {surveyTemplates.find(s => s.id === formData.surveyType)?.description}
                </p>
                <p className="survey-time">
                  Estimated time: {surveyTemplates.find(s => s.id === formData.surveyType)?.estimatedTime || '10-15 minutes'}
                </p>
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="surveyTitle">Survey Title/Identifier</label>
            <input
              type="text"
              id="surveyTitle"
              name="surveyTitle"
              value={formData.surveyTitle}
              onChange={handleChange}
              placeholder="E.g., Initial Assessment, Follow-up 1, etc."
            />
            <small className="form-hint">Add a custom title to identify this specific instance (optional)</small>
          </div>

          <div className="form-group">
            <label htmlFor="patientId">Select Patient *</label>
            <select
              id="patientId"
              name="patientId"
              value={formData.patientId}
              onChange={handleChange}
              required
              disabled={selectedPatient}
            >
              <option value="">Choose a patient...</option>
              {patients.map((patient) => (
                <option key={patient.id} value={patient.id}>
                  {patient.name} - {patient.email}
                </option>
              ))}
            </select>
            {selectedPatient && (
              <p className="selected-patient-info">
                Selected: {selectedPatient.name}
              </p>
            )}
          </div>



          <div className="form-row">
            <div className="form-group">
              <label htmlFor="dueDate">Due Date *</label>
              <input
                type="date"
                id="dueDate"
                name="dueDate"
                value={formData.dueDate}
                onChange={handleChange}
                min={getMinDate()}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="priority">Priority</label>
              <select
                id="priority"
                name="priority"
                value={formData.priority}
                onChange={handleChange}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="notes">Additional Notes</label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows="3"
              placeholder="Any additional instructions or notes for the patient..."
            />
          </div>

          <div className="form-group">
            <label>Repeat Survey</label>
            <div className="form-row">
              <div className="form-group">
                <select
                  id="repeat"
                  name="repeat"
                  value={formData.repeat}
                  onChange={handleChange}
                  className="form-control"
                >
                  <option value="none">Don't repeat</option>
                  <option value="weekly">Every week</option>
                  <option value="biweekly">Every 2 weeks</option>
                  <option value="monthly">Every month</option>
                </select>
              </div>
              {formData.repeat !== 'none' && (
                <div className="form-group">
                  <input
                    type="date"
                    id="repeatEndDate"
                    name="repeatEndDate"
                    value={formData.repeatEndDate}
                    onChange={handleChange}
                    min={formData.dueDate}
                    className="form-control"
                    placeholder="End date (optional)"
                  />
                  <small className="form-hint">Leave empty for no end date</small>
                </div>
              )}
            </div>
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="form-actions">
            <button type="button" onClick={onClose} className="cancel-btn">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="assign-btn">
              {loading ? 'Assigning...' : 'Assign Survey'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AssignSurveyModal;
